/**
 * SortableList Component
 *
 * A reusable drag-and-drop list container using @dnd-kit.
 * Compound component pattern with SortableList.Item subcomponent.
 *
 * Features:
 * - Auto-generated IDs via WeakMap (no id field required on items)
 * - Built-in drag handle
 * - Standard visual feedback (opacity 0.6, scale 1.03)
 * - Vertical/horizontal/grid strategies
 * - Keyboard accessibility
 * - Theme-aware styling
 *
 * @example
 * ```tsx
 * <SortableList items={items} onReorder={setItems}>
 *   {(item) => (
 *     <SortableList.Item>
 *       <YourContent />
 *     </SortableList.Item>
 *   )}
 * </SortableList>
 * ```
 */

import React, { useCallback, useMemo, memo } from 'react';
import { Box } from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  rectSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { SortableListContext } from './SortableListContext';
import { SortableListItem } from './SortableListItem';
import { DragHandle } from './DragHandle';
import { useStableIds } from './useStableIds';
import type { SortableListProps, SortableListContextValue } from './SortableList.types';

/**
 * Gap size mapping to theme.spacing values
 */
const gapMap = {
  sm: 1,  // theme.spacing(1) = 8px
  md: 2,  // theme.spacing(2) = 16px
  lg: 3,  // theme.spacing(3) = 24px
} as const;

/**
 * Get strategy function based on strategy prop
 */
function getStrategy(strategy: 'vertical' | 'horizontal' | 'grid') {
  switch (strategy) {
    case 'horizontal':
      return horizontalListSortingStrategy;
    case 'grid':
      return rectSortingStrategy;
    default:
      return verticalListSortingStrategy;
  }
}

/**
 * Props for ItemWithContext component
 */
interface ItemWithContextProps<T extends object> {
  item: T;
  index: number;
  disabled: boolean;
  getStableId: (item: object) => string;
  renderChild: (item: T, index: number) => React.ReactNode;
}

/**
 * Internal component for a single item with memoized context value.
 * This prevents context value recreation on every parent render.
 */
function ItemWithContextBase<T extends object>({
  item,
  index,
  disabled,
  getStableId,
  renderChild,
}: ItemWithContextProps<T>) {
  // Memoize context value to prevent unnecessary re-renders of children
  const contextValue = useMemo<SortableListContextValue>(
    () => ({
      disabled,
      getItemId: getStableId,
      currentItem: item,
      currentIndex: index,
    }),
    [disabled, getStableId, item, index]
  );

  return (
    <SortableListContext.Provider value={contextValue}>
      {renderChild(item, index)}
    </SortableListContext.Provider>
  );
}

// Memoize the component to prevent re-renders when parent re-renders
const ItemWithContext = memo(ItemWithContextBase) as <T extends object>(
  props: ItemWithContextProps<T>
) => React.ReactElement;

/**
 * Internal component for rendering items with context
 */
function ItemRenderer<T extends object>({
  items,
  children,
  getStableId,
  disabled,
}: {
  items: T[];
  children: (item: T, index: number) => React.ReactNode;
  getStableId: (item: object) => string;
  disabled: boolean;
}) {
  return (
    <>
      {items.map((item, index) => (
        <ItemWithContext
          key={getStableId(item)}
          item={item}
          index={index}
          disabled={disabled}
          getStableId={getStableId}
          renderChild={children}
        />
      ))}
    </>
  );
}

/**
 * SortableList - Main container component
 */
function SortableListBase<T extends object>({
  items,
  onReorder,
  children,
  gap = 'sm',
  strategy = 'vertical',
  disabled = false,
  sx,
  'data-testid': testId,
}: SortableListProps<T>) {
  const { getStableId } = useStableIds();

  // Sensors for pointer and keyboard interaction
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end - reorder items
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = items.findIndex((item) => getStableId(item) === active.id);
      const newIndex = items.findIndex((item) => getStableId(item) === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(arrayMove(items, oldIndex, newIndex));
      }
    },
    [items, onReorder, getStableId]
  );

  // Get sorting strategy
  const sortingStrategy = useMemo(() => getStrategy(strategy), [strategy]);

  // Get item IDs for SortableContext
  const itemIds = useMemo(
    () => items.map((item) => getStableId(item)),
    [items, getStableId]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={itemIds} strategy={sortingStrategy}>
        <Box
          role="list"
          data-testid={testId}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: gapMap[gap],
            ...sx,
          }}
        >
          <ItemRenderer
            items={items}
            children={children}
            getStableId={getStableId}
            disabled={disabled}
          />
        </Box>
      </SortableContext>
    </DndContext>
  );
}

// Type for compound component
interface SortableListComponent {
  <T extends object>(props: SortableListProps<T>): React.ReactElement;
  Item: typeof SortableListItem;
  DragHandle: typeof DragHandle;
}

// Attach subcomponents
const SortableList = SortableListBase as SortableListComponent;
SortableList.Item = SortableListItem;
SortableList.DragHandle = DragHandle;

export { SortableList };
